import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/shop.scss";

const uniqueValues = (values) => [...new Set(values.filter(Boolean))];
const normalizeValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const colorMap = new Map();
productData.forEach((product) => {
  (product.colors || []).forEach((color, index) => {
    if (!colorMap.has(color)) {
      colorMap.set(color, product.colorHex?.[index] || "#b7b7b0");
    }
  });
});

const filterOptions = {
  genders: uniqueValues(productData.map((product) => product.gender)).sort(),
  categories: uniqueValues(
    productData.map((product) => product.category)
  ).sort(),
  colors: [...colorMap.entries()]
    .map(([name, hex]) => ({ name, hex }))
    .sort((a, b) => a.name.localeCompare(b.name)),
  sizes: uniqueValues(productData.flatMap((product) => product.size || [])),
};

const productPrices = productData.map((product) => Number(product.price) || 0);
const priceBounds = {
  min: Math.floor(Math.min(...productPrices)),
  max: Math.ceil(Math.max(...productPrices)),
};
const currencySymbol =
  formatPrice(0).replace(/[\d\s.,\u00a0]/g, "") || siteConfig.currency;

const getDisplayImage = (source) =>
  source ? source.replace(/\.webp(?=($|\?))/, ".jpg") : "";

const getProductLabels = (product) => {
  const labels = [];
  if (product.compareAt && Number(product.compareAt) > Number(product.price)) {
    labels.push({ kind: "sale", text: "Sale" });
  }
  if (product.isNew) labels.push({ kind: "new", text: "New" });
  if (!labels.length && product.featured) {
    labels.push({ kind: "featured", text: "Featured" });
  }
  return labels.slice(0, 2);
};

export const ProductCard = ({ product }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const displayImage = getDisplayImage(product.image || product.images?.[0]);

  const labels = getProductLabels(product);
  const discount = product.compareAt
    ? Math.round((1 - Number(product.price) / Number(product.compareAt)) * 100)
    : 0;

  useEffect(() => {
    setImageLoaded(false);
    setImageError(!displayImage);
  }, [displayImage, product.id]);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <article className="product-card">
      <div className="product-card__media">
        <Link
          className="product-card__image-link"
          to={`/shopDetails/${product.id}`}
          onClick={scrollToTop}
          aria-label={`View ${product.name}`}
        >
          {!imageLoaded && !imageError && (
            <span className="product-card__skeleton" aria-hidden="true" />
          )}
          {imageError ? (
            <span
              className="product-card__image-error"
              role="img"
              aria-label="Product image unavailable"
            >
              <span aria-hidden="true">{siteConfig.brandName.slice(0, 1)}</span>
              Image unavailable
            </span>
          ) : (
            <img
              className={imageLoaded ? "is-loaded" : "is-loading"}
              src={displayImage}
              alt={product.name}
              loading="lazy"
              decoding="async"
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                setImageError(true);
                setImageLoaded(true);
              }}
            />
          )}
        </Link>

        {labels.length > 0 && (
          <div className="product-card__labels" aria-label="Product labels">
            {labels.map((label) => (
              <span
                className={`product-card__label product-card__label--${label.kind}`}
                key={label.kind}
              >
                {label.text}
                {label.kind === "sale" && discount > 0 ? ` −${discount}%` : ""}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="product-card__body">
        <p className="product-card__meta">
          {product.gender} · {product.category}
        </p>
        <Link
          className="product-card__name"
          to={`/shopDetails/${product.id}`}
          onClick={scrollToTop}
        >
          {product.name}
        </Link>
        <div className="product-card__footer">
          <div className="product-card__prices">
            <span
              className={
                product.compareAt
                  ? "product-card__price product-card__price--sale"
                  : "product-card__price"
              }
            >
              {formatPrice(product.price)}
            </span>
            {product.compareAt && <del>{formatPrice(product.compareAt)}</del>}
          </div>
          <div
            className="product-card__colors"
            aria-label={`${product.colors?.length || 0} available colours`}
          >
            {(product.colors || []).slice(0, 4).map((color, index) => (
              <span
                className="product-card__color"
                key={color}
                title={color}
                style={{
                  "--swatch-color": product.colorHex?.[index] || "#b7b7b0",
                }}
              />
            ))}
            {(product.colors || []).length > 4 && (
              <span className="product-card__color-more">
                +{product.colors.length - 4}
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [availability, setAvailability] = useState("all");
  const [minimumPrice, setMinimumPrice] = useState(String(priceBounds.min));
  const [maximumPrice, setMaximumPrice] = useState(String(priceBounds.max));
  const [sortBy, setSortBy] = useState("featured");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const query = searchParams.get("q") || "";
  const genderParam = searchParams.get("gender") || "";
  const selectedGender =
    filterOptions.genders.find(
      (gender) => normalizeValue(gender) === normalizeValue(genderParam)
    ) || "";
  const requestedCategories = searchParams
    .getAll("category")
    .flatMap((category) => category.split(","))
    .map(normalizeValue);
  const selectedCategories = filterOptions.categories.filter((category) =>
    requestedCategories.includes(normalizeValue(category))
  );

  useEffect(() => {
    if (!filtersOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleEscape = (event) => {
      if (event.key === "Escape") setFiltersOpen(false);
    };
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [filtersOpen]);

  const setUrlFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    next.delete(key);

    if (Array.isArray(value)) {
      value.forEach((item) => next.append(key, item));
    } else if (value) {
      next.set(key, value);
    }

    setSearchParams(next, { replace: true });
  };

  const toggleSelection = (value, selected, setSelected) => {
    setSelected(
      selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value]
    );
  };

  const toggleCategory = (category) => {
    const nextCategories = selectedCategories.includes(category)
      ? selectedCategories.filter((item) => item !== category)
      : [...selectedCategories, category];
    setUrlFilter("category", nextCategories);
  };

  const safeMinimumPrice =
    minimumPrice === "" ? priceBounds.min : Number(minimumPrice);
  const safeMaximumPrice =
    maximumPrice === "" ? priceBounds.max : Number(maximumPrice);

  const filteredProducts = useMemo(() => {
    const normalizedQuery = normalizeValue(query);
    const products = productData.filter((product) => {
      const searchableText = normalizeValue(
        [
          product.name,
          product.sku,
          product.gender,
          product.category,
          product.attributes?.material,
          ...(product.tags || []),
          ...(product.colors || []),
        ].join(" ")
      );
      const matchesQuery =
        !normalizedQuery || searchableText.includes(normalizedQuery);
      const matchesGender =
        !selectedGender || product.gender === selectedGender;
      const matchesCategory =
        !selectedCategories.length ||
        selectedCategories.includes(product.category);
      const matchesColor =
        !selectedColors.length ||
        selectedColors.some((color) => product.colors?.includes(color));
      const matchesSize =
        !selectedSizes.length ||
        selectedSizes.some((size) => product.size?.includes(size));
      const inStock = product.inStock !== false;
      const matchesAvailability =
        availability === "all" ||
        (availability === "in-stock" && inStock) ||
        (availability === "out-of-stock" && !inStock);
      const price = Number(product.price);
      const matchesPrice =
        price >= safeMinimumPrice && price <= safeMaximumPrice;

      return (
        matchesQuery &&
        matchesGender &&
        matchesCategory &&
        matchesColor &&
        matchesSize &&
        matchesAvailability &&
        matchesPrice
      );
    });

    return [...products].sort((a, b) => {
      if (sortBy === "price-low") return Number(a.price) - Number(b.price);
      if (sortBy === "price-high") return Number(b.price) - Number(a.price);
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "newest") {
        return (
          Number(Boolean(b.isNew)) - Number(Boolean(a.isNew)) || b.id - a.id
        );
      }

      const score = (product) =>
        Number(Boolean(product.featured)) * 4 +
        Number(Boolean(product.isNew)) * 2 +
        Number(Boolean(product.compareAt));
      return score(b) - score(a) || a.id - b.id;
    });
  }, [
    availability,
    query,
    selectedCategories,
    selectedColors,
    selectedGender,
    selectedSizes,
    sortBy,
    safeMaximumPrice,
    safeMinimumPrice,
  ]);

  const hasCustomPrice =
    minimumPrice !== String(priceBounds.min) ||
    maximumPrice !== String(priceBounds.max);
  const activeFilterCount =
    Number(Boolean(query)) +
    Number(Boolean(selectedGender)) +
    selectedCategories.length +
    selectedColors.length +
    selectedSizes.length +
    Number(availability !== "all") +
    Number(hasCustomPrice);

  const resetFilters = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setSelectedColors([]);
    setSelectedSizes([]);
    setAvailability("all");
    setMinimumPrice(String(priceBounds.min));
    setMaximumPrice(String(priceBounds.max));
    setSortBy("featured");
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main className="shop" id="main-content">
      <div className="shop__shell">
        <nav className="shop__breadcrumb" aria-label="Breadcrumb">
          <Link to="/" onClick={scrollToTop}>
            Home
          </Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">Shop</span>
        </nav>

        <section className="shop-hero" aria-labelledby="shop-title">
          <div className="shop-hero__image">
            <img
              src="/main/shop-hero.jpg"
              alt="Models wearing understated everyday layers in a gallery"
            />
          </div>
          <div className="shop-hero__copy">
            <p>{siteConfig.tagline}</p>
            <h1 id="shop-title">The collection</h1>
            <span>{siteConfig.description}</span>
          </div>
        </section>

        <section className="shop__catalog" aria-label="Product catalogue">
          <div className="shop__catalog-grid">
            <button
              className="shop__filter-backdrop"
              type="button"
              aria-label="Close filters"
              onClick={() => setFiltersOpen(false)}
              hidden={!filtersOpen}
            />

            <aside
              className={`shop-filters${filtersOpen ? " is-open" : ""}`}
              id="shop-filters"
              aria-label="Product filters"
              role={filtersOpen ? "dialog" : undefined}
              aria-modal={filtersOpen ? "true" : undefined}
              aria-labelledby={
                filtersOpen ? "shop-filter-drawer-title" : undefined
              }
            >
              <div className="shop-filters__drawer-head">
                <strong id="shop-filter-drawer-title">Filters</strong>
                <button
                  type="button"
                  onClick={() => setFiltersOpen(false)}
                  aria-label="Close filters"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>

              <div className="shop-filters__heading">
                <span>Filter</span>
                {activeFilterCount > 0 && (
                  <button type="button" onClick={resetFilters}>
                    Reset all
                  </button>
                )}
              </div>

              <div className="shop-filters__search">
                <label htmlFor="shop-search">Search products</label>
                <div>
                  <input
                    id="shop-search"
                    type="search"
                    value={query}
                    onChange={(event) => setUrlFilter("q", event.target.value)}
                    placeholder="Search by name or material"
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => setUrlFilter("q", "")}
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              <details className="shop-filter" open>
                <summary>
                  Gender <span>{selectedGender ? "1" : ""}</span>
                </summary>
                <div className="shop-filter__options">
                  <label>
                    <input
                      type="radio"
                      name="gender"
                      checked={!selectedGender}
                      onChange={() => setUrlFilter("gender", "")}
                    />
                    <span>All</span>
                  </label>
                  {filterOptions.genders.map((gender) => (
                    <label key={gender}>
                      <input
                        type="radio"
                        name="gender"
                        checked={selectedGender === gender}
                        onChange={() => setUrlFilter("gender", gender)}
                      />
                      <span>{gender}</span>
                    </label>
                  ))}
                </div>
              </details>

              <details className="shop-filter" open>
                <summary>
                  Category <span>{selectedCategories.length || ""}</span>
                </summary>
                <div className="shop-filter__options">
                  {filterOptions.categories.map((category) => (
                    <label key={category}>
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category)}
                        onChange={() => toggleCategory(category)}
                      />
                      <span>{category}</span>
                    </label>
                  ))}
                </div>
              </details>

              <details className="shop-filter" open>
                <summary>
                  Size <span>{selectedSizes.length || ""}</span>
                </summary>
                <div className="shop-filter__sizes">
                  {filterOptions.sizes.map((size) => (
                    <label key={size}>
                      <input
                        type="checkbox"
                        checked={selectedSizes.includes(size)}
                        onChange={() =>
                          toggleSelection(size, selectedSizes, setSelectedSizes)
                        }
                      />
                      <span>{size}</span>
                    </label>
                  ))}
                </div>
              </details>

              <details className="shop-filter" open>
                <summary>
                  Availability <span>{availability !== "all" ? "1" : ""}</span>
                </summary>
                <div className="shop-filter__options">
                  {[
                    ["all", "All products"],
                    ["in-stock", "In stock"],
                    ["out-of-stock", "Out of stock"],
                  ].map(([value, label]) => (
                    <label key={value}>
                      <input
                        type="radio"
                        name="availability"
                        checked={availability === value}
                        onChange={() => setAvailability(value)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </details>

              <details className="shop-filter shop-filter--price" open>
                <summary>
                  Price <span>{hasCustomPrice ? "1" : ""}</span>
                </summary>
                <div className="shop-filter__price-fields">
                  <label>
                    <span>From</span>
                    <div>
                      <b>{currencySymbol}</b>
                      <input
                        type="number"
                        min={priceBounds.min}
                        max={priceBounds.max}
                        value={minimumPrice}
                        onChange={(event) =>
                          setMinimumPrice(event.target.value)
                        }
                      />
                    </div>
                  </label>
                  <span aria-hidden="true">—</span>
                  <label>
                    <span>To</span>
                    <div>
                      <b>{currencySymbol}</b>
                      <input
                        type="number"
                        min={priceBounds.min}
                        max={priceBounds.max}
                        value={maximumPrice}
                        onChange={(event) =>
                          setMaximumPrice(event.target.value)
                        }
                      />
                    </div>
                  </label>
                </div>
                <input
                  className="shop-filter__range"
                  type="range"
                  min={priceBounds.min}
                  max={priceBounds.max}
                  value={maximumPrice || priceBounds.max}
                  onChange={(event) => setMaximumPrice(event.target.value)}
                  aria-label="Maximum price"
                />
              </details>

              <button
                className="shop-filters__apply"
                type="button"
                onClick={() => setFiltersOpen(false)}
              >
                Show {filteredProducts.length}{" "}
                {filteredProducts.length === 1 ? "product" : "products"}
              </button>
            </aside>

            <div className="shop-products">
              <div className="shop-products__toolbar">
                <div className="shop-products__count" aria-live="polite">
                  <strong>{filteredProducts.length}</strong>
                  <span>
                    {filteredProducts.length === 1 ? "product" : "products"}
                  </span>
                </div>
                <button
                  className="shop-products__mobile-filter"
                  type="button"
                  onClick={() => setFiltersOpen(true)}
                  aria-controls="shop-filters"
                  aria-expanded={filtersOpen}
                >
                  Filters{" "}
                  {activeFilterCount > 0 && <span>{activeFilterCount}</span>}
                </button>
                <label className="shop-products__sort">
                  <span>Sort by</span>
                  <select
                    value={sortBy}
                    onChange={(event) => setSortBy(event.target.value)}
                  >
                    <option value="featured">Featured</option>
                    <option value="newest">Newest</option>
                    <option value="price-low">Price: low to high</option>
                    <option value="price-high">Price: high to low</option>
                    <option value="name">Name: A–Z</option>
                  </select>
                </label>
              </div>

              {filteredProducts.length > 0 ? (
                <div className="shop-products__grid">
                  {filteredProducts.map((product) => (
                    <ProductCard product={product} key={product.id} />
                  ))}
                </div>
              ) : (
                <div className="shop-products__empty" role="status">
                  <span aria-hidden="true">0</span>
                  <h2>No pieces match those filters.</h2>
                  <p>
                    Try widening the price range or removing one of your
                    selections.
                  </p>
                  <button type="button" onClick={resetFilters}>
                    Reset filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

export default Shop;
